import config from '../config/config';
import AuthService from './AuthService';
import { selectedOrganizationVar } from '../graphql/local/Category/vars';
import { Organization } from '../Utils/Organization';

export interface CgvStylesPayload {
  styledTitle?: string;
  styledTextContainer?: string;
  styledArticleTitle?: string;
  styledTextPadding?: string;
  styledUlsStyle?: string;
  styledLiStyle?: string;
  styledLiParagraph?: string;
  styledTextCenter?: string;
  styledNoticeSpacing?: string;
}

export const DEFAULT_CGV_STYLES: CgvStylesPayload = {
  styledTitle:
    "font-family:'PT Serif';font-style:normal;font-weight:bold;font-size:1.5rem;text-align:center;line-height:1.33;",
  styledTextContainer: 'margin-bottom:3em;margin-top:2em;font-size:1rem;',
  styledArticleTitle: 'font-weight:bold;',
  styledTextPadding: 'padding-top:2em;',
  styledUlsStyle: 'padding-left:0;',
  styledLiStyle: 'list-style:none;',
  styledLiParagraph: 'display:inline;margin:0;',
  styledTextCenter: 'font-weight:bold;text-align:center;',
  styledNoticeSpacing: 'margin-top:0.5rem;margin-bottom:0.5rem;',
};

const getHeaders = (): Record<string, string> => {
  const token = AuthService.getAccessToken();
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    requestHeaders.Authorization = `Bearer ${token}`;
  }
  const switchUser = AuthService.getSwitchUser();
  if (switchUser) {
    requestHeaders['x-switch-user'] = switchUser;
  }
  return requestHeaders;
};

export const resolveCurrentOrganizationId = async (): Promise<number | string | null> => {
  const selectedOrgId = selectedOrganizationVar()?.id;
  if (selectedOrgId) return selectedOrgId;

  const orgFromUtils = Organization.getOrganization()?.id;
  if (orgFromUtils) return orgFromUtils;

  const adminOrgId = AuthService.getOrganizationIDByAdmin();
  if (adminOrgId) return adminOrgId;

  const token = AuthService.getAccessToken();
  if (!token) return null;

  try {
    const baseApi = config.apiSymphonyDigidom || 'https://dev.api.digidom.pro/v1';
    const res = await fetch(`${baseApi}/me`, {
      headers: getHeaders(),
    });
    if (res.ok) {
      const data = await res.json();
      if (data?.id) {
        const userRes = await fetch(`${baseApi}/users/${data.id}`, {
          headers: getHeaders(),
        });
        if (userRes.ok) {
          const userData = await userRes.json();
          if (Array.isArray(userData?.organizations) && userData.organizations.length > 0) {
            const firstOrg = userData.organizations[0];
            const resolvedId =
              firstOrg?.id || (typeof firstOrg === 'string' ? firstOrg.split('/').pop() : null);
            if (resolvedId) return resolvedId;
          }
        }
      }
    }
  } catch (err) {
    console.error('Failed to auto-resolve organization ID for CGV:', err);
  }

  return null;
};

export const fetchCgvFromApi = async (
  organizationId: number | string,
  customStyles?: Partial<CgvStylesPayload>,
): Promise<string> => {
  const baseApi = config.apiSymphonyDigidom || 'https://dev.api.digidom.pro/v1';
  const url = `${baseApi}/organizations/${organizationId}/legal/cgv`;
  const body = JSON.stringify({
    ...DEFAULT_CGV_STYLES,
    ...customStyles,
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch CGV (HTTP ${response.status})`);
  }

  return response.text();
};
